import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './destination-pages.api';

import type {
  CreateWebsiteDestinationPageRequest,
  UpdateWebsiteDestinationPageRequest,
  WebsiteDestinationPageListQuery,
} from '@utazzo/shared';

const QK = {
  list: (query: WebsiteDestinationPageListQuery) => ['destination-pages', 'list', query] as const,
  detail: (id: string) => ['destination-pages', 'detail', id] as const,
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};

export const useDestinationPagesList = (query: WebsiteDestinationPageListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.list(query),
    staleTime: 30_000,
  });

export const useDestinationPageDetail = (id: string | undefined) =>
  useQuery({
    queryKey: id ? QK.detail(id) : ['destination-pages', 'detail', 'none'],
    queryFn: () => api.get(id as string),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

export const useCollectionCandidates = (destinationId: string | undefined) =>
  useQuery({
    queryKey: ['destination-pages', 'collection-candidates', destinationId],
    queryFn: () => api.listCollectionCandidates(destinationId as string),
    enabled: Boolean(destinationId),
    staleTime: 60_000,
  });

export const useCreateDestinationPage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateWebsiteDestinationPageRequest) => api.create(payload),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['destination-pages', 'list'] });
      toast.success(`Created ${p.title}`);
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to create destination page'),
  });
};

export const useUpdateDestinationPage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateWebsiteDestinationPageRequest }) =>
      api.update(id, payload),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['destination-pages', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(p.id) });
      toast.success('Destination page saved');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to save destination page'),
  });
};

export const useDeleteDestinationPage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['destination-pages', 'list'] });
      toast.success('Destination page deleted');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete destination page'),
  });
};

export const usePublishDestinationPage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.publish(id),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['destination-pages', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(p.id) });
      toast.success('Published');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to publish'),
  });
};

export const useUnpublishDestinationPage = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.unpublish(id),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['destination-pages', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(p.id) });
      toast.success('Unpublished');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to unpublish'),
  });
};
